import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Nav from "@/components/Nav";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Copy, ArrowLeft, Flag, ExternalLink, Linkedin, Users, Wand2, History, Pencil } from "lucide-react";
import { toast } from "sonner";
import { replacePlaceholders, buildPlaceholderMap } from "@/lib/placeholders";

const STATUS_OPTS = [
  ["new", "New"],
  ["contacted", "Contacted"],
  ["replied", "Replied"],
  ["meeting_booked", "Meeting booked"],
  ["not_interested", "Not interested"],
];

function appendSignature(body, signature) {
  const base = (body || "").replace(/\s+$/, "");
  const sig = (signature || "").trim();
  if (!sig) return base;
  return base ? `${base}\n\n${sig}` : sig;
}

function renderForCopy(draft, signature, lead, user) {
  // Substitute both the draft body AND the signature using the lead + user context.
  const draftR = replacePlaceholders(draft || "", lead, user);
  const sigR = replacePlaceholders(signature || "", lead, user);
  return appendSignature(draftR, sigR);
}

export default function LeadDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [lead, setLead] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [history, setHistory] = useState([]);

  const signature = (user?.email_signature || "").trim();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/leads/${id}`);
        setLead(data);
        setNotes(data.notes || "");
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Could not load lead");
      }
      try {
        const { data } = await api.get(`/leads/${id}/history`);
        setHistory(data);
      } catch { /* ignore */ }
    })();
  }, [id]);

  const update = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/leads/${id}`, patch);
      setLead(data);
      if (patch.status !== undefined) {
        try {
          const h = await api.get(`/leads/${id}/history`);
          setHistory(h.data);
        } catch { /* ignore */ }
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text || "");
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed");
    }
  };

  const reenrich = async () => {
    setEnriching(true);
    try {
      const { data } = await api.post(`/leads/${id}/enrich`);
      setLead(data);
      toast.success("Lead re-enriched");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Enrichment failed");
    } finally {
      setEnriching(false);
    }
  };

  if (!lead) return <div className="min-h-screen"><Nav /><div className="max-w-5xl mx-auto px-6 py-10 text-muted-foreground">Loading…</div></div>;

  return (
    <div className="min-h-screen">
      <Nav />
      <main className="max-w-5xl mx-auto px-6 py-10">
        <button onClick={() => nav(-1)} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 mb-6" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="flex flex-wrap items-start justify-between gap-6">
          <div className="min-w-0 flex-1">
            <div className="overline text-primary">Lead</div>
            <h1 className="mt-2 text-4xl font-medium tracking-tighter">{lead.company}</h1>
            <div className="mt-2 text-sm text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1">
              <span>{lead.location || "—"}</span>
              <span className="opacity-40">·</span>
              <span className="inline-flex items-center gap-1">
                <Users className="w-3.5 h-3.5 opacity-70" />
                {lead.headcount ? `${lead.headcount} people` : (lead.est_size || "size unknown")}
              </span>
              <span className="opacity-40">·</span>
              <span>{lead.decision_maker_role || "—"}</span>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              {lead.website && (
                <a
                  href={lead.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                  data-testid="lead-website-link"
                >
                  Website <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
              {lead.linkedin_url && (
                <a
                  href={lead.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                  data-testid="lead-linkedin-link"
                >
                  <Linkedin className="w-3.5 h-3.5" /> LinkedIn
                </a>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              className="rounded-full border-primary/40 text-primary hover:bg-primary/10"
              onClick={reenrich}
              disabled={enriching}
              data-testid="reenrich-btn"
            >
              <Wand2 className="w-3.5 h-3.5 mr-1.5" /> {enriching ? "Re-enriching…" : "Re-enrich"}
            </Button>
            <div className="rounded-xl border border-border bg-[#121214] px-5 py-3 text-center">
              <div className="text-xs text-muted-foreground uppercase tracking-[0.18em]">Fit score</div>
              <div className="mt-1 text-3xl font-medium text-primary mono">{lead.fit_score}</div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mt-10">
          <div className="md:col-span-2 space-y-6">
            <div className="rounded-xl border border-border bg-[#121214] p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="font-medium">Why this is a good fit</div>
              </div>
              <p className="text-sm leading-relaxed text-muted-foreground">{lead.why_good_fit || "—"}</p>
            </div>

            <div className="rounded-xl border border-border bg-[#121214] p-6">
              <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                <div className="font-medium">Draft email</div>
                <div className="flex items-center gap-2">
                  {signature ? (
                    <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.14em] text-primary bg-primary/10 border border-primary/20 rounded-full px-2 py-0.5" data-testid="signature-attached-pill">
                      <Pencil className="w-3 h-3" /> Signature attached
                    </span>
                  ) : (
                    <Button asChild size="sm" variant="link" className="text-primary text-xs px-0" data-testid="signature-setup-link">
                      <Link to="/account">+ Add signature</Link>
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => copy(renderForCopy(lead.draft_email, signature, lead, user), "Email")} data-testid="copy-email-btn">
                    <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy
                  </Button>
                </div>
              </div>
              <pre className="whitespace-pre-wrap text-sm text-muted-foreground font-sans leading-relaxed" data-testid="draft-email-body">{replacePlaceholders(lead.draft_email || "—", lead, user)}</pre>
              {signature && (
                <div className="mt-4 pt-4 border-t border-dashed border-primary/30">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-2">Your signature (variables filled)</div>
                  <pre className="whitespace-pre-wrap text-sm text-foreground font-sans leading-relaxed" data-testid="draft-email-signature">{replacePlaceholders(signature, lead, user)}</pre>
                </div>
              )}
            </div>

            <div className="rounded-xl border border-border bg-[#121214] p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="font-medium">LinkedIn connection note</div>
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => copy(lead.draft_linkedin, "LinkedIn note")} data-testid="copy-linkedin-btn">
                  <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy
                </Button>
              </div>
              <pre className="whitespace-pre-wrap text-sm text-muted-foreground font-sans leading-relaxed">{lead.draft_linkedin || "—"}</pre>
            </div>
          </div>

          <aside className="space-y-6">
            <div className="rounded-xl border border-border bg-[#121214] p-6">
              <div className="font-medium mb-3">Pipeline status</div>
              <Select value={lead.status} onValueChange={(v) => update({ status: v })} disabled={saving}>
                <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTS.map(([v, l]) => (
                    <SelectItem key={v} value={v} data-testid={`status-opt-${v}`}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="rounded-xl border border-border bg-[#121214] p-6">
              <div className="font-medium mb-3">Notes</div>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                onBlur={() => notes !== lead.notes && update({ notes })}
                rows={6}
                placeholder="Private notes about this lead…"
                data-testid="notes-textarea"
              />
            </div>

            <div className="rounded-xl border border-border bg-[#121214] p-6">
              <div className="font-medium mb-3">Source</div>
              {lead.source_url ? (
                <a href={lead.source_url} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline break-all" data-testid="source-link">
                  {lead.source_url}
                </a>
              ) : <div className="text-sm text-muted-foreground">—</div>}

              <Button
                variant="outline"
                className="w-full mt-4 rounded-full border-red-500/30 text-red-400 hover:bg-red-500/10"
                onClick={() => update({ reported: !lead.reported }).then(() => toast.success(lead.reported ? "Report cleared" : "Lead reported"))}
                data-testid="report-btn"
              >
                <Flag className="w-4 h-4 mr-1.5" /> {lead.reported ? "Reported" : "Report incorrect lead"}
              </Button>
            </div>
            <div className="rounded-xl border border-border bg-[#121214] p-6" data-testid="lead-history">
              <div className="flex items-center justify-between mb-4">
                <div className="font-medium flex items-center gap-2"><History className="w-4 h-4 text-primary" /> Engagement timeline</div>
              </div>
              {history.length === 0 ? (
                <div className="text-sm text-muted-foreground">No status changes yet. Move this lead to Contacted to start the timeline.</div>
              ) : (
                <ol className="relative pl-5 before:absolute before:left-1.5 before:top-1.5 before:bottom-1.5 before:w-px before:bg-border space-y-4">
                  {history.map((h) => (
                    <li key={h.id} className="relative" data-testid={`history-${h.id}`}>
                      <span className="absolute -left-5 top-1.5 w-3 h-3 rounded-full bg-primary/20 border-2 border-primary"></span>
                      <div className="text-sm">
                        <span className="capitalize text-muted-foreground">{h.from_status.replace("_", " ")}</span>
                        <span className="text-muted-foreground mx-2">→</span>
                        <span className="capitalize text-foreground font-medium">{h.to_status.replace("_", " ")}</span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5 mono">{new Date(h.created_at).toLocaleString()}</div>
                    </li>
                  ))}
                </ol>
              )}
            </div>

          </aside>
        </div>

        <p className="mt-10 text-xs text-muted-foreground">AI-generated; verify before contacting.</p>
      </main>
    </div>
  );
}
